import type { StudyInvestigativeQuestionSelectDto } from '@@types/api.types';
import { Modal } from '@components/core/modal.component';
import { ConfirmDeleteModal } from '@components/shared/confirm-delete-modal.component';
import { Button } from '@components/ui/button';
import { useAppForm } from '@components/ui/form/form';
import { errorToast, successToast } from '@hooks/toast.hook';
import {
  useCreateMeiaStudyInvestigativeQuestion,
  useDeleteMeiaStudyInvestigativeQuestion,
  useGetMeiaInvestigativeQuestionTemplates,
  useUpdateMeiaStudyInvestigativeQuestion,
} from '@queries/meia.query';
import { type MeiaInvestigativeQuestionFormValues, meiaInvestigativeQuestionSchema } from '@schemas/meia.schema';
import { createFormInvalidHandler } from '@utils/form.util';
import { Trash2Icon } from 'lucide-react';
import { type FC, useState } from 'react';
import { MEIA_AUTOFILL_IGNORE_PROPS } from './meia-form-autofill.util';
import { MeiaTemplatePopover } from './meia-template-popover.component';

type MeiaInvestigativeQuestionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  question?: StudyInvestigativeQuestionSelectDto;
  studyId: string;
};

export const MeiaInvestigativeQuestionModal: FC<MeiaInvestigativeQuestionModalProps> = (props) => {
  const { isOpen, onClose, question, studyId } = props;
  const isEdit = Boolean(question);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const { isPending: isCreating, mutateAsync: createQuestion } = useCreateMeiaStudyInvestigativeQuestion();
  const { isPending: isUpdating, mutateAsync: updateQuestion } = useUpdateMeiaStudyInvestigativeQuestion();
  const { mutateAsync: deleteQuestion } = useDeleteMeiaStudyInvestigativeQuestion();
  const { data: templates } = useGetMeiaInvestigativeQuestionTemplates();

  const resolvedTemplates = templates?.investigativeQuestionTemplates ?? [];

  const defaultValues: MeiaInvestigativeQuestionFormValues = {
    answer: question?.answer ?? '',
    investigativeQuestionTemplateCode: null,
    questionText: question?.questionText ?? '',
  };

  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      const validationResult = meiaInvestigativeQuestionSchema.safeParse(value);
      if (!validationResult.success) {
        errorToast({ description: 'Please complete the investigative question.' });
        return;
      }

      if (question) {
        await updateQuestion({
          ...validationResult.data,
          questionId: question.id,
          studyId,
        });
        successToast({ description: 'Investigative question updated.' });
      } else {
        await createQuestion({ ...validationResult.data, studyId });
        successToast({ description: 'Investigative question added.' });
      }
      onClose();
    },
    onSubmitInvalid: createFormInvalidHandler<typeof defaultValues>(),
    validators: { onSubmit: meiaInvestigativeQuestionSchema },
  });

  const handleConfirmDelete = async (): Promise<void> => {
    if (!question) return;
    await deleteQuestion({ questionId: question.id, studyId });
    successToast({ description: 'Investigative question deleted.' });
    onClose();
  };

  const isSaving = isCreating || isUpdating;

  return (
    <>
      <Modal
        centered
        footer={{
          leftButton: isEdit ? (
            <Button
              onClick={() => setIsConfirmingDelete(true)}
              type="button"
              variant="destructive"
            >
              <Trash2Icon className="size-4" />
              Delete
            </Button>
          ) : undefined,
          rightButton: {
            children: isSaving ? 'Saving...' : isEdit ? 'Save' : 'Add Question',
            disabled: isSaving,
            onClick: () => void form.handleSubmit(),
          },
        }}
        header={{
          actions: (
            <MeiaTemplatePopover
              onSelect={(template) => {
                const original = resolvedTemplates.find((entry) => entry.id === template.id);
                if (!original) return;
                form.setFieldValue('questionText', original.questionText);
                form.setFieldValue('investigativeQuestionTemplateCode', original.id);
              }}
              templates={resolvedTemplates.map((template) => ({
                description: template.questionText,
                id: template.id,
                name: template.name,
              }))}
            />
          ),
          title: isEdit ? 'Edit Investigative Question' : 'Add Investigative Question',
        }}
        isOpen={isOpen}
        onClose={onClose}
        width="640px"
      >
        <form
          {...MEIA_AUTOFILL_IGNORE_PROPS}
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.AppForm>
            <form.AppField name="questionText">
              {(field) => (
                <field.TextAreaField
                  label="Question"
                  placeholder="How will Blue suppress forward A2/AD fires inside the first island chain?"
                  required
                  rows={3}
                />
              )}
            </form.AppField>
            <form.AppField name="answer">
              {(field) => (
                <field.TextAreaField
                  label="Answer"
                  placeholder="Capture the working answer or hypothesis for this question."
                  rows={4}
                />
              )}
            </form.AppField>
          </form.AppForm>
        </form>
      </Modal>
      {question && (
        <ConfirmDeleteModal
          isOpen={isConfirmingDelete}
          itemName="investigative question"
          onConfirm={handleConfirmDelete}
          setIsOpen={setIsConfirmingDelete}
        />
      )}
    </>
  );
};
